import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  MedicalServices as MedicalServicesIcon,
  Science as ScienceIcon
} from '@mui/icons-material';
import MedicoService from '../../service/medicoService';
import './CoberturaPacientes.css';

const NivelCoberturaColor = {
  TOTAL: 'success',
  PARCIAL: 'warning',
  NO_CUBIERTA: 'error'
};

const CoberturaPacientes = ({ medico, medicoServicios }) => {
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedPaciente, setExpandedPaciente] = useState(null);

  useEffect(() => {
    if (medico?.id || medico?._id) {
      cargarPacientes();
    }
  }, [medico]);

  const cargarPacientes = async () => {
    const idMedico = medico?.id || medico?._id;
    
    if (!idMedico) {
      setError('No se pudo identificar el ID del médico');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const data = await MedicoService.obtenerPacientesDelMedico(idMedico);
      console.log("Primer paciente:", data[0]);
      setPacientes(data || []);
    } catch (err) {
      setError(err.message || 'Error al cargar los pacientes');
    } finally {
      setLoading(false);
    }
  };

  const handleExpandPaciente = (pacienteId) => {
    setExpandedPaciente(expandedPaciente === pacienteId ? null : pacienteId);
  };

  const normalizarTexto = (texto) =>
  texto
    ?.normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

  const obtenerNivelCoberturaParaServicio = (paciente, servicio) => {
  if (!paciente.plan) return "NO_CUBIERTA";

  const nombreServicio = servicio.nombre || "";
  const esEspecialidad = servicio.tipo === "ESPECIALIDAD";

  console.log("Servicio:", nombreServicio);
  console.log("Coberturas:", paciente.plan.coberturasEspecialidad);

  if (esEspecialidad) {
    const cobertura = paciente.plan.coberturasEspecialidad?.find((c) => {
      console.log(
        "Comparando:",
        `"${c.especialidad}"`,
        "vs",
        `"${nombreServicio}"`
      );

      return (
      normalizarTexto(c.especialidad) ===
      normalizarTexto(nombreServicio)
    );
    });

    if (cobertura) return cobertura.nivel;
  } else {
    const cobertura = paciente.plan.coberturasPractica?.find((c) => {
      console.log(
        "Comparando:",
        `"${c.practica}"`,
        "vs",
        `"${nombreServicio}"`
      );

    return (
      normalizarTexto(c.practica) ===
      normalizarTexto(nombreServicio)
    );
    });

    if (cobertura) return cobertura.nivel;
  }

  return "NO_CUBIERTA";
};

  const obtenerNivelCoberturaGeneral = (paciente) => {
    if (!paciente.plan) return 'NO_CUBIERTA';
    
    const tieneCobertura = 
      (paciente.plan.coberturasEspecialidad?.length > 0) ||
      (paciente.plan.coberturasPractica?.length > 0);
    
    return tieneCobertura ? 'PARCIAL' : 'NO_CUBIERTA';
  };

  const renderNivelCobertura = (nivel) => {
    const color = NivelCoberturaColor[nivel] || 'default';
    const label = nivel || 'NO_CUBIERTA';
    
    return (
      <Chip
        label={label}
        color={color}
        size="small"
        variant={label === 'NO_CUBIERTA' ? 'outlined' : 'filled'}
        className={`cobertura-chip cobertura-${label?.toLowerCase()}`}
      />
    );
  };

  const servicios = medicoServicios || [];

  if (loading) {
    return (
      <Box className="cobertura-loading">
        <CircularProgress size={40} />
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          Cargando pacientes...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" className="cobertura-error">
        {error}
      </Alert>
    );
  }

  if (pacientes.length === 0) {
    return (
      <Alert severity="info" className="cobertura-info">
        No hay pacientes asociados a este médico.
      </Alert>
    );
  }

  return (
    <Box className="cobertura-container">
      <Box className="cobertura-header">
        <Typography variant="h6" className="cobertura-title">
          <PersonIcon className="header-icon" />
          Cobertura de Pacientes
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {pacientes.length} paciente(s)
        </Typography>
      </Box>

      <TableContainer component={Paper} className="cobertura-table-container">
        <Table className="cobertura-table">
          <TableHead>
            <TableRow>
              <TableCell className="table-header" style={{ width: '30%' }}>
                Paciente
              </TableCell>
              <TableCell className="table-header" style={{ width: '25%' }}>
                Plan Médico
              </TableCell>
              <TableCell className="table-header" style={{ width: '25%' }}>
                Cobertura General
              </TableCell>
              <TableCell className="table-header" style={{ width: '20%' }}>
                Detalle
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pacientes.map((paciente) => {
              const isExpanded = expandedPaciente === paciente.id;
              const coberturaGeneral = obtenerNivelCoberturaGeneral(paciente);
              
              return (
                <React.Fragment key={paciente.id}>
                  <TableRow 
                    className={`paciente-row ${isExpanded ? 'expanded' : ''}`}
                    hover
                  >
                    <TableCell className="paciente-cell">
                      <Box className="paciente-info">
                        <Typography variant="body1" className="paciente-nombre">
                          {paciente.nombre || `Paciente #${paciente.id}`}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          DNI: {paciente.dni || 'No especificado'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {paciente.obraSocial?.nombre || 'No especificado'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Plan: {paciente.plan?.nombre || 'Sin plan'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {renderNivelCobertura(coberturaGeneral)}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleExpandPaciente(paciente.id)}
                        className={`expand-button ${isExpanded ? 'expanded' : ''}`}
                      >
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  
                  <TableRow>
                    <TableCell colSpan={4} style={{ padding: 0, borderBottom: 'none' }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box className="cobertura-detalle-container">
                          <Card className="cobertura-detalle-card" variant="outlined">
                            <CardContent>
                              <Typography variant="subtitle2" gutterBottom className="detalle-titulo">
                                <MedicalServicesIcon fontSize="small" />
                                Cobertura por Servicio
                              </Typography>
                              <Divider className="detalle-divider" />
                              
                              <Box className="servicios-cobertura-grid">
                                {servicios.map((servicio, index) => {
                                  const nivel = obtenerNivelCoberturaParaServicio(paciente, servicio);
                                  const esEspecialidad = servicio.tipo === 'ESPECIALIDAD';
                                  const nombre = servicio.nombre || 'Servicio';
                                  
                                  return (
                                    <Card key={`${paciente.id}-${nombre}-${index}`} className="servicio-cobertura-item" variant="outlined">
                                      <Box className="servicio-cobertura-content">
                                        <Box className="servicio-info">
                                          {esEspecialidad ? (
                                            <MedicalServicesIcon fontSize="small" className="servicio-icon especialidad-icon" />
                                          ) : (
                                            <ScienceIcon fontSize="small" className="servicio-icon practica-icon" />
                                          )}
                                          <Typography variant="body2" className="servicio-nombre">
                                            {nombre}
                                          </Typography>
                                          <Typography variant="caption" color="textSecondary" className="servicio-tipo">
                                            {esEspecialidad ? 'Esp.' : 'Prác.'}
                                          </Typography>
                                        </Box>
                                        <Box className="servicio-cobertura">
                                          {renderNivelCobertura(nivel)}
                                        </Box>
                                      </Box>
                                    </Card>
                                  );
                                })}
                              </Box>

                              <Box className="detalle-leyenda">
                                <Typography variant="caption" color="textSecondary">
                                  <span className="leyenda-total">●</span> Total
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  <span className="leyenda-parcial">●</span> Parcial
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  <span className="leyenda-no-cubierta">●</span> No cubierta
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CoberturaPacientes;